import { useState, useCallback } from 'react'
import { api, type ApiError } from '../api/client.js'

export interface ReassignmentConversation {
  id: string
  referenceId: string
  channelOrigin: string
  status: string
  subject: string | null
  description: string | null
  primaryHandle: string | null
  displayName: string | null
  assignedAgentId: string | null
  assignedAgentLoginId: string | null
  assignedTeamId: string | null
  priorityLabel: string | null
  source: string | null
  openedAt: string | null
  lastMessageAt: string | null
  isArchived: boolean
}

export interface ReassignmentSearchFilters {
  referenceId?: string
  subject?: string
  description?: string
  channel?: string
  status?: string
  stateId?: string
  typeId?: string
  categoryId?: string
  subcategoryId?: string
  priorityId?: string
  impactId?: string
  urgencyId?: string
  complexityId?: string
  approvalStateId?: string
  approvalLevelId?: string
  assignedAgentId?: string
  assignedTeamId?: string
  campaignId?: string
  locationId?: string
  branchId?: string
  departmentId?: string
  from?: string
  to?: string
  dynamicProperty?: string
  dynamicPropertyValue?: string
}

/**
 * A bulk action's payload. Ownership fields and classification properties are
 * both optional — the caller sends only what the agent actually set, and the
 * API applies exactly those to every selected conversation.
 */
export interface ReassignTarget {
  assignedTeamId?: string
  assignedAgentId?: string
  campaignId?: string
  locationId?: string
  branchId?: string
  departmentId?: string
  priorityId?: string
  /** The UI's "State" — conversations.status_id, matching the detail panel. */
  statusId?: string
  categoryId?: string
  subcategoryId?: string
  typeId?: string
  subtypeId?: string
  impactId?: string
  urgencyId?: string
  complexityId?: string
}

export interface ReassignResult {
  successCount: number
  errorCount: number
  errors?: { conversationId: string; error: string }[]
}

/** Set when the API refused because the target agent would go over their cap. */
export interface CapacityBlock {
  message: string
  currentLoad: number
  maxLoad: number
  requested: number
}

/**
 * Returned by `reassign` so the caller can react in the same tick. State
 * alone wouldn't do: a caller awaiting `reassign` still holds the render's
 * closure, so it would read the previous value of `capacityBlock`.
 */
export type ReassignOutcome =
  | { kind: 'ok'; result: ReassignResult }
  | { kind: 'capacity'; block: CapacityBlock }
  | { kind: 'error'; message: string }

export function useReassignment() {
  const [conversations, setConversations] = useState<ReassignmentConversation[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [reassigning, setReassigning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [capacityBlock, setCapacityBlock] = useState<CapacityBlock | null>(null)

  const search = useCallback(async (filters: ReassignmentSearchFilters) => {
    setLoading(true)
    setError(null)
    setSearched(true)
    try {
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value)
        }
      })
      params.append('limit', '100')
      params.append('offset', '0')

      const res = await api.get<{ items: ReassignmentConversation[]; totalCount: number }>(
        `/conversations?${params.toString()}`,
      )
      setConversations(res.items || [])
      setTotalCount(res.totalCount || 0)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to search conversations')
      setConversations([])
      setTotalCount(0)
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * `force` pushes past the target agent's conversation limit. The API answers a
   * plain 409 AGENT_AT_CAPACITY, which is surfaced through `capacityBlock` so
   * the caller can show the numbers and offer to retry with force — rather
   * than reporting it as a generic failure.
   */
  const reassign = useCallback(
    async (
      conversationIds: string[],
      target: ReassignTarget,
      opts?: { force?: boolean },
    ): Promise<ReassignOutcome> => {
      setReassigning(true)
      setError(null)
      setCapacityBlock(null)
      try {
        const res = await api.post<ReassignResult>('/conversations/reassign', {
          conversationIds,
          ...target,
          ...(opts?.force ? { force: true } : {}),
        })
        return { kind: 'ok', result: res }
      } catch (e) {
        const body = (e as ApiError | undefined)?.body as
          | { error?: string; message?: string; currentLoad?: number; maxLoad?: number; requested?: number }
          | undefined
        if (body?.error === 'AGENT_AT_CAPACITY') {
          const block: CapacityBlock = {
            message: body.message ?? 'That agent is already at their conversation limit.',
            currentLoad: body.currentLoad ?? 0,
            maxLoad: body.maxLoad ?? 0,
            requested: body.requested ?? conversationIds.length,
          }
          setCapacityBlock(block)
          return { kind: 'capacity', block }
        }
        const message = e instanceof Error ? e.message : 'Failed to reassign conversations'
        setError(message)
        return { kind: 'error', message }
      } finally {
        setReassigning(false)
      }
    },
    [],
  )

  const reset = useCallback(() => {
    setConversations([])
    setTotalCount(0)
    setSearched(false)
    setError(null)
    setCapacityBlock(null)
  }, [])

  return {
    conversations,
    totalCount,
    loading,
    searched,
    reassigning,
    error,
    capacityBlock,
    clearCapacityBlock: () => setCapacityBlock(null),
    search,
    reassign,
    reset,
  }
}
